import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertCircle, Users, FileText, Clock, CheckCheck, X, Info } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';

interface CombinedNotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  created_at: string;
  related_id?: string;
  source: 'general' | 'admin';
  company_id?: string;
}

interface NotificationDropdownProps {
  notifications: CombinedNotification[];
  unreadCount: number;
  isLoading: boolean;
  onMarkAsRead: (notificationId: string, source: 'general' | 'admin') => Promise<void>;
  onMarkAllAsRead: () => Promise<void>;
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount,
  isLoading,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose
}) => {
  const navigate = useNavigate();

  const handleNotificationClick = async (notification: CombinedNotification) => {
    if (!notification.is_read) {
      await onMarkAsRead(notification.id, notification.source);
    }

    // Navigate based on notification type and source
    if (notification.source === 'admin' && notification.notification_type === 'reactivation_request') {
      // Navigate to admin panel reactivation requests
      navigate('/admin?tab=reactivation-requests');
    } else if (notification.related_id) {
      // Handle general notifications with related_id navigation
      // This can be extended based on notification types
    }
    
    onClose();
  };

  const getNotificationIcon = (type: string, source: 'general' | 'admin') => {
    if (source === 'admin') {
      switch (type) {
        case 'reactivation_request':
          return <Users className="h-4 w-4 text-blue-500" />;
        default:
          return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      }
    } else {
      switch (type) {
        case 'system':
          return <AlertCircle className="h-4 w-4 text-yellow-500" />;
        case 'document':
          return <FileText className="h-4 w-4 text-green-500" />;
        case 'general':
          return <Info className="h-4 w-4 text-blue-500" />;
        default:
          return <Bell className="h-4 w-4 text-gray-500" />;
      }
    }
  };

  if (isLoading) {
    return (
      <div className="w-80 p-4">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-white shadow-lg rounded-lg border">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMarkAllAsRead}
                className="text-xs h-8"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <ScrollArea className="max-h-96">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-sm">No notifications yet</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              You'll see important updates here
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !notification.is_read ? 'bg-blue-50/50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.notification_type, notification.source)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className={`text-sm font-medium truncate ${
                        !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                      }`}>
                        {notification.title}
                      </h4>
                      <div className="flex items-center gap-1">
                        {!notification.is_read && (
                          <Badge variant="destructive" className="h-2 w-2 p-0" />
                        )}
                        {notification.source === 'admin' && (
                          <Badge variant="secondary" className="text-xs">Admin</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {notifications.length > 0 && notifications.some(n => n.source === 'admin') && (
        <>
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                navigate('/admin');
                onClose();
              }}
              className="w-full justify-center text-sm"
            >
              View all in admin panel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};